import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Modal, Button } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import AutomationDesigner from './components/AutomationDesigner';
import LeadAutomation from './components/LeadAutomation';
import FollowUpTemplate from './components/FollowUpTemplate';
import NurturixCustomTabs from '../../components/common/NurturixCustomTabs';

const Automation = () => {
  const [activeTab, setActiveTab] = useState('webinars');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { key: 'webinars', label: 'Webinars Settings' },
    { key: 'leads', label: 'Leads' },
    { key: 'template', label: 'Templates' }
  ];

  // Function to handle unsaved changes detection
  const handleUnsavedChanges = (hasChanges) => {
    setHasUnsavedChanges(hasChanges);
  };

  // Function to handle tab change with unsaved changes check
  const handleTabChange = (newTab) => {
    if (hasUnsavedChanges && activeTab === 'webinars') {
      setPendingNavigation({ type: 'tab', target: newTab });
      setShowUnsavedModal(true);
    } else {
      setActiveTab(newTab);
    }
  };

  // Function to handle browser navigation/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    const handlePopState = (e) => {
      if (hasUnsavedChanges) {
        const confirmLeave = window.confirm(
          'You have unsaved automation changes. Are you sure you want to leave this page?'
        );
        if (!confirmLeave) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', location.pathname);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, location.pathname]);

  // Handle modal actions
  const handleSaveAndContinue = () => {
    // Here you would typically save the changes
    // For now, we'll just simulate saving
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    
    // Execute the pending navigation
    if (pendingNavigation) {
      if (pendingNavigation.type === 'tab') {
        setActiveTab(pendingNavigation.target);
      }
      setPendingNavigation(null);
    }
  };

  const handleDiscardChanges = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedModal(false);
    
    // Execute the pending navigation
    if (pendingNavigation) {
      if (pendingNavigation.type === 'tab') {
        setActiveTab(pendingNavigation.target);
      }
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowUnsavedModal(false);
    setPendingNavigation(null);
  };

  return (
    <div className="page-top pb-5">
      <Container fluid>
        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            <h1 className="page-title mb-1">Automation Suite</h1>
            <p className="page-subtitle">
              Create powerful automated workflows to streamline your lead generation and engagement processes
            </p>
          </Col>
        </Row>

        {/* Navigation Tabs */}
        <Row className="mb-4">
          <Col>
            <NurturixCustomTabs 
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </Col>
        </Row>

        {/* Tab Content */}
        <Row>
          <Col>
            {activeTab === 'webinars' && (
              <AutomationDesigner onUnsavedChanges={handleUnsavedChanges} />
            )}
            {activeTab === 'leads' && (
              <LeadAutomation/>
            )}
            {activeTab === 'template' && (
              <FollowUpTemplate/>
            )}
          </Col>
        </Row>
      </Container>

      {/* Unsaved Changes Modal */}
      <Modal show={showUnsavedModal} onHide={handleCancelNavigation} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
            Unsaved Changes Detected
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <p className="mb-2">
              <strong>You have unsaved automation configuration changes:</strong>
            </p>
            <ul className="text-muted mb-3">
              <li>Checkbox selections for email/WhatsApp communications</li>
              <li>Custom timing configurations</li>
              <li>Automation feature settings</li>
            </ul>
            <p className="text-danger small mb-0">
              <i className="bi bi-info-circle me-1"></i>
              If you continue without saving, all your changes will be lost.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCancelNavigation}>
            <i className="bi bi-arrow-left me-1"></i>
            Stay on Page
          </Button>
          <Button variant="outline-danger" onClick={handleDiscardChanges}>
            <i className="bi bi-trash me-1"></i>
            Discard Changes
          </Button>
          <Button variant="success" onClick={handleSaveAndContinue}>
            <i className="bi bi-check-circle me-1"></i>
            Save & Continue
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Automation; 